// frontend/app/components/lessonPlans/LessonQuizPlayer.js
//
// The topic quiz, played inline inside the lesson step — the third member of
// the set with LessonPwoPlayer and LessonVideoPlayer.
//
// Before this, a `saras_topic_quiz` block was a doorway: it flipped
// `activeModule` and handed the student to the global quiz player. The answers
// went into the general quiz tables, so they moved no lesson mastery and never
// appeared in the lesson's Insights. The lesson could not see its own check.
//
// Two things make this different from the players either side of it:
//
//   * It renders through `QuestionDisplay` — the app's own question renderer.
//     A bank question can be a fill-in-the-blank, an MCQ, a true/false or a
//     multi-select, with hints, a worked solution and images in the stem;
//     re-implementing that here would mean a question that looks one way from
//     the topic tile and another way inside a lesson.
//   * It records. Every answer goes up as `{ questionId, value, isCorrect }`
//     and is stored under its own composite block id, which is what makes a
//     twelve-question quiz twelve pieces of evidence rather than one.
//
// It must never trap a student. The quiz can always be closed, a load failure
// says so and leaves the step usable, and lesson completion rules are advisory
// — a quiz that will not load cannot stop the lesson being finished.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import QuestionDisplay from "../../QuestionDisplay";
import { gradeQuizAnswer, quizTally } from "./lessonQuizGrading";
import { loadLessonBlockQuiz } from "./lessonQuizSource";

const LessonQuizPlayer = ({
  lessonPlanId,
  blockId,
  stepKey = null,
  //: `{ [questionId]: value }` — answers already recorded for this block, so
  //: coming back to the step shows the student's own work rather than a blank
  //: quiz. Read once, at mount: after that this component owns the answers.
  initialAnswers = null,
  authToken,
  languageId = 1,
  isDarkMode = false,
  readOnly = false,
  onAnswer,
  onClose,
  t,
}) => {
  const translate = t || ((value) => value);

  const containerRef = useRef(null);

  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [totalAvailable, setTotalAvailable] = useState(0);

  const [userAnswers, setUserAnswers] = useState(() => ({ ...(initialAnswers || {}) }));
  // The same answers, readable synchronously. A multi-select toggle and a
  // text submit both need the answer *as it stands* to build the next one, and
  // reading it out of a `setState` updater would mean reporting from inside
  // one — which React is free to run twice.
  const answersRef = useRef({ ...(initialAnswers || {}) });
  const [answerFeedback, setAnswerFeedback] = useState({});
  const [showHints, setShowHints] = useState({});
  const [showSolutions, setShowSolutions] = useState({});
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    let disposed = false;

    (async () => {
      setStatus("loading");
      setErrorMessage(null);

      try {
        const source = await loadLessonBlockQuiz({
          lessonPlanId,
          blockId,
          stepKey,
          languageId,
          authToken,
        });
        if (disposed) return;
        setQuestions(source.questions);
        setTotalAvailable(source.totalQuestionCount);
        setStatus("ready");
      } catch (error) {
        if (disposed) return;
        console.error("[LESSON QUIZ] could not load questions:", error);
        setStatus("error");
        setErrorMessage(error?.message || "Could not load this quiz.");
      }
    })();

    return () => {
      disposed = true;
    };
  }, [lessonPlanId, blockId, stepKey, languageId, authToken]);

  // Bring the quiz into view when it opens — it renders inline part-way down
  // the step, which on a long step can be below the fold. Web only: elsewhere
  // (and under jest-expo) a View ref is not a DOM node, so this is a no-op.
  useEffect(() => {
    const node = containerRef.current;
    if (!node || typeof node.scrollIntoView !== "function") return undefined;
    const raf = requestAnimationFrame(() => {
      try {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        node.scrollIntoView();
      }
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const questionsById = useMemo(() => {
    const index = {};
    questions.forEach((question) => {
      index[question?.question_id] = question;
    });
    return index;
  }, [questions]);

  /**
   * Record one answer: locally so the student sees it, and upward so it
   * becomes lesson evidence.
   *
   * Grading happens here rather than on the server for the same reason it does
   * for an authored `multiple_choice`: the key is already on the client, and
   * the recording endpoint takes `is_correct` from the client for every other
   * check in the lesson.
   */
  const record = useCallback(
    (questionId, value) => {
      const question = questionsById[questionId];
      const isCorrect = gradeQuizAnswer(question, value);

      answersRef.current = { ...answersRef.current, [questionId]: value };
      setUserAnswers(answersRef.current);
      setAnswerFeedback((previous) => ({
        ...previous,
        [questionId]:
          isCorrect === null ? "ungraded" : isCorrect ? "correct" : "wrong",
      }));

      // A teacher walking the quiz on the projector, or an authoring preview,
      // answers freely and records nothing.
      if (readOnly || !onAnswer) return;
      onAnswer({ questionId, value, isCorrect });
    },
    [onAnswer, questionsById, readOnly]
  );

  // ---- QuestionDisplay's handler contract --------------------------------
  const handleMCQSelection = useCallback(
    (questionId, selectedOption) => record(questionId, selectedOption),
    [record]
  );

  const handleMultiSelect = useCallback(
    (questionId, selectedOption) => {
      const current = Array.isArray(answersRef.current[questionId])
        ? answersRef.current[questionId]
        : [];
      // The whole selection is the answer, not the option just tapped: a
      // multi-select is graded as a set.
      const next = current.includes(selectedOption)
        ? current.filter((key) => key !== selectedOption)
        : [...current, selectedOption];
      record(questionId, next);
    },
    [record]
  );

  // Typing is not answering: a half-written word must not be graded and sent.
  // `checkTextAnswer` is what QuestionDisplay calls on blur / submit.
  const handleAnswerChange = useCallback((questionId, value) => {
    answersRef.current = { ...answersRef.current, [questionId]: value };
    setUserAnswers(answersRef.current);
  }, []);

  const checkTextAnswer = useCallback(
    (questionId) => record(questionId, answersRef.current[questionId]),
    [record]
  );

  const toggleHint = useCallback(
    (questionId) =>
      setShowHints((previous) => ({
        ...previous,
        [questionId]: !previous[questionId],
      })),
    []
  );

  const toggleSolution = useCallback(
    (questionId) =>
      setShowSolutions((previous) => ({
        ...previous,
        [questionId]: !previous[questionId],
      })),
    []
  );

  const tally = useMemo(
    () => quizTally(questions, userAnswers),
    [questions, userAnswers]
  );

  const header = (
    <View style={[styles.header, { borderBottomColor: isDarkMode ? "#1e293b" : "#e2e8f0" }]}>
      <MaterialCommunityIcons name="clipboard-check-outline" size={18} color="#8b5cf6" />
      <View style={styles.headerText}>
        <Text style={[styles.title, { color: isDarkMode ? "#e2e8f0" : "#0f172a" }]}>
          {translate("Topic Quiz")}
        </Text>
        {status === "ready" ? (
          <>
            <Text style={[styles.subtitle, { color: isDarkMode ? "#94a3b8" : "#64748b" }]}>
              {`${tally.answered}/${tally.total} ${translate("answered")}`}
            </Text>
            {/* Only when the author capped the quiz: a teacher seeing "5" of a
                33-question bank should be able to tell it was a choice. */}
            {totalAvailable > tally.total ? (
              <Text style={[styles.note, { color: isDarkMode ? "#64748b" : "#94a3b8" }]}>
                {`${translate("Showing")} ${tally.total} ${translate("of")} ${totalAvailable} ${translate("questions in this topic")}`}
              </Text>
            ) : null}
          </>
        ) : null}
      </View>

      {status === "ready" && !revealed && tally.answered > 0 ? (
        <TouchableOpacity
          onPress={() => setRevealed(true)}
          accessibilityRole="button"
          accessibilityLabel={translate("Check answers")}
          style={[styles.checkButton, { backgroundColor: "#8b5cf6" }]}
        >
          <Text style={styles.checkButtonText}>{translate("Check answers")}</Text>
        </TouchableOpacity>
      ) : null}

      {onClose ? (
        <TouchableOpacity
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={translate("Close quiz")}
          style={[styles.close, { backgroundColor: isDarkMode ? "#1e293b" : "#e2e8f0" }]}
        >
          <MaterialCommunityIcons
            name="close"
            size={16}
            color={isDarkMode ? "#e2e8f0" : "#334155"}
          />
        </TouchableOpacity>
      ) : null}
    </View>
  );

  return (
    <View
      ref={containerRef}
      style={[
        styles.container,
        {
          backgroundColor: isDarkMode ? "#0f172a" : "#ffffff",
          borderColor: isDarkMode ? "#1e293b" : "#cbd5e1",
        },
      ]}
    >
      {header}

      {status === "loading" ? (
        <View style={styles.state}>
          <ActivityIndicator size="large" color="#8b5cf6" />
          <Text style={[styles.stateTitle, { color: isDarkMode ? "#e2e8f0" : "#334155" }]}>
            {translate("Loading quiz...")}
          </Text>
        </View>
      ) : null}

      {status === "error" ? (
        <View style={styles.state}>
          <MaterialCommunityIcons name="alert-circle-outline" size={28} color="#f87171" />
          <Text style={[styles.stateTitle, { color: isDarkMode ? "#e2e8f0" : "#334155" }]}>
            {translate("This quiz could not be opened")}
          </Text>
          {errorMessage ? (
            <Text style={[styles.stateDetail, { color: isDarkMode ? "#94a3b8" : "#64748b" }]}>
              {errorMessage}
            </Text>
          ) : null}
          {/* The lesson goes on without it: completion rules are advisory and
              a quiz that will not load must not hold a class up. */}
          <Text style={[styles.stateDetail, { color: isDarkMode ? "#94a3b8" : "#64748b" }]}>
            {translate("You can close this and carry on with the lesson.")}
          </Text>
        </View>
      ) : null}

      {status === "ready" ? (
        <View style={styles.body}>
          <QuestionDisplay
            mode="quizPractice"
            quiz={questions}
            isDarkMode={isDarkMode}
            userAnswers={userAnswers}
            answerFeedback={answerFeedback}
            showHints={showHints}
            showSolutions={showSolutions}
            toggleHint={toggleHint}
            toggleSolution={toggleSolution}
            handleAnswerChange={handleAnswerChange}
            handleMCQSelection={handleMCQSelection}
            handleMultiSelect={handleMultiSelect}
            checkTextAnswer={checkTextAnswer}
            // Nothing is coloured while the student is still choosing — the
            // deferred behaviour the app moved to deliberately, and the only
            // one that works for multi-select, where revealing early would
            // mark every correct option before the second tap.
            //
            // After "Check answers" the marks and the correct options are
            // shown. A student may still change an answer from there; the
            // server counts that as a further attempt and the attempt penalty
            // takes the credit down accordingly.
            immediateFeedback={false}
            isReviewMode={revealed}
          />
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
    maxWidth: 1100,
    alignSelf: "center",
    borderRadius: 14,
    borderWidth: 1,
    overflow: "hidden",
    marginBottom: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerText: { flex: 1, minWidth: 0, marginLeft: 10 },
  title: { fontSize: 14, fontWeight: "800" },
  subtitle: { fontSize: 12, marginTop: 2 },
  note: { fontSize: 11, marginTop: 1 },
  checkButton: {
    borderRadius: 999,
    paddingHorizontal: 14,
    paddingVertical: 7,
    marginRight: 8,
  },
  checkButtonText: { color: "#ffffff", fontSize: 12, fontWeight: "700" },
  close: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: "center",
    justifyContent: "center",
  },
  body: { padding: 14 },
  state: {
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingVertical: 36,
  },
  stateTitle: { fontSize: 14, fontWeight: "700", marginTop: 10, textAlign: "center" },
  stateDetail: { fontSize: 12, marginTop: 4, textAlign: "center" },
});

export default LessonQuizPlayer;
